import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar, Users, FloppyDisk, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { initials } from "@/lib/format";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const PERIODS = ["AM", "PM"];
const PERIOD_LABEL = { AM: "09:00 – 13:00", PM: "13:00 – 17:00" };

export default function AdminSchedule() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Agents + admins can edit the roster; only admins manage team membership.
  const canEditSchedule = user?.role === "admin" || user?.role === "agent";
  const [slots, setSlots] = useState([]);
  const [team, setTeam] = useState([]);
  const [current, setCurrent] = useState(null);
  const [users, setUsers] = useState([]);
  const [teamOpen, setTeamOpen] = useState(false);
  const [teamPicked, setTeamPicked] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [savingTeam, setSavingTeam] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    const [s, t, c] = await Promise.all([
      api.get("/schedule"),
      api.get("/support-team"),
      api.get("/schedule/current"),
    ]);
    setSlots(s.data);
    setTeam(t.data);
    setCurrent(c.data);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (teamOpen) {
      api.get("/users").then((r) => {
        // Only allow active users of role admin/agent
        setUsers(r.data.filter((u) => u.is_active !== false && (u.role === "admin" || u.role === "agent")));
        setTeamPicked(new Set(team.map((u) => u.id)));
      });
    }
  }, [teamOpen, team]);

  const teamById = useMemo(() => Object.fromEntries(team.map((u) => [u.id, u])), [team]);

  const setSlotAssignee = (day, period, uid) => {
    setSlots((cur) => cur.map((s) => (s.day_of_week === day && s.period === period ? { ...s, assigned_user_id: uid } : s)));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/schedule", { slots });
      toast.success("Schedule saved");
      setDirty(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const saveTeam = async () => {
    setSavingTeam(true);
    try {
      await api.patch("/support-team", { user_ids: Array.from(teamPicked) });
      toast.success("Support team updated");
      setTeamOpen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingTeam(false); }
  };

  const togglePicked = (id) => {
    setTeamPicked((cur) => {
      const n = new Set(cur);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  return (
    <div className="space-y-4" data-testid="schedule-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            <Calendar size={22} weight="duotone" className="text-[#4f4c90]" /> Support Schedule
          </h1>
          <p className="text-sm text-slate-500 mt-1">Weekly on-support rota (Mon–Fri, AM/PM). Assignees receive a 15-minute email warning before their slot starts.</p>
        </div>
        {canEditSchedule && (
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Dialog open={teamOpen} onOpenChange={setTeamOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="h-9 gap-1 border-slate-200 bg-white" data-testid="edit-support-team-btn">
                    <Users size={14} weight="duotone" /> Support Team ({team.length})
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-slate-200 max-w-md">
                  <DialogHeader><DialogTitle>Select support team members</DialogTitle></DialogHeader>
                  <div className="text-xs text-slate-500 mb-2">Only team members can be assigned to schedule slots.</div>
                  <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                    {users.length === 0 && <div className="text-sm text-slate-500 py-3">No eligible users.</div>}
                    {users.map((u) => (
                      <label key={u.id} className="flex items-center gap-3 py-2 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded" data-testid={`support-team-toggle-${u.email}`}>
                        <Checkbox checked={teamPicked.has(u.id)} onCheckedChange={() => togglePicked(u.id)} />
                        <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-[11px]">{initials(u.name)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{u.name}</div>
                          <div className="text-[11px] text-slate-500 font-mono truncate">{u.email}</div>
                        </div>
                        <span className="chip capitalize text-[10px] !py-0.5">{u.role}</span>
                      </label>
                    ))}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" className="border-slate-200 bg-white h-9" onClick={() => setTeamOpen(false)}>Cancel</Button>
                    <Button onClick={saveTeam} disabled={savingTeam} className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9" data-testid="support-team-save">
                      {savingTeam ? "Saving…" : "Save team"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            <Button onClick={save} disabled={!dirty || saving} className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9 gap-1" data-testid="schedule-save">
              <FloppyDisk size={14} weight="duotone" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </Button>
          </div>
        )}
      </div>

      {current?.active && current?.assignee && (
        <div className="hairline rounded-lg p-3 bg-[#39A36E]/10 border-[#39A36E]/30 flex items-center gap-3" data-testid="current-support-banner">
          <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-xs font-medium border border-[#39A36E]/40">
            {initials(current.assignee.name)}
          </div>
          <div className="text-sm">
            <span className="font-medium text-[#265b41]">{current.assignee.name}</span>
            <span className="text-slate-600"> is currently on support ({current.period} · {PERIOD_LABEL[current.period]})</span>
          </div>
        </div>
      )}

      {team.length === 0 && (
        <div className="hairline rounded-lg p-3 bg-amber-50 border-amber-200 text-sm text-amber-900 flex items-start gap-2">
          <Info size={16} weight="duotone" className="mt-0.5 flex-shrink-0" />
          <div>No one is on the support team yet. Click <b>Support Team</b> above to add members, then assign them to slots.</div>
        </div>
      )}

      <div className="hairline rounded-lg bg-white overflow-hidden">
        <div className="grid grid-cols-[80px_repeat(5,1fr)] border-b border-slate-200 bg-slate-50">
          <div className="p-3 text-[11px] uppercase tracking-wider text-slate-500 font-medium">Slot</div>
          {DAYS.map((d) => (
            <div key={d} className="p-3 text-[11px] uppercase tracking-wider text-slate-500 font-medium border-l border-slate-200">
              {d}
            </div>
          ))}
        </div>
        {PERIODS.map((period) => (
          <div key={period} className="grid grid-cols-[80px_repeat(5,1fr)] border-b border-slate-100 last:border-0">
            <div className="p-3 flex flex-col justify-center bg-slate-50/60">
              <div className="text-sm font-medium text-slate-800">{period}</div>
              <div className="text-[10px] text-slate-500 font-mono">{PERIOD_LABEL[period]}</div>
            </div>
            {DAYS.map((_, day) => {
              const slot = slots.find((s) => s.day_of_week === day && s.period === period);
              const assigneeId = slot?.assigned_user_id || "__none__";
              const assignee = assigneeId !== "__none__" ? teamById[assigneeId] : null;
              return (
                <div key={`${day}-${period}`} className="p-2 border-l border-slate-100" data-testid={`slot-${day}-${period}`}>
                  {canEditSchedule ? (
                    <Select value={assigneeId} onValueChange={(v) => setSlotAssignee(day, period, v === "__none__" ? null : v)}>
                      <SelectTrigger className="w-full bg-white border-slate-200 h-9 text-xs" data-testid={`slot-select-${day}-${period}`}>
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="__none__">— Unassigned —</SelectItem>
                        {team.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="p-2 text-sm">
                      {assignee ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px]">{initials(assignee.name)}</div>
                          <span className="truncate">{assignee.name}</span>
                        </div>
                      ) : <span className="text-slate-400 italic text-xs">Unassigned</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="text-[11px] text-slate-500 font-mono">Times shown in <b>Europe/London</b> (or your configured business timezone). Emails are sent 15 minutes before each slot starts to the assigned user.</div>
    </div>
  );
}
